import moment from "moment";
import { SessionRow, createEmptySessionRow, sessionStart, sortSessionRows } from "./SessionList";

/**
 * Calendrier du mois de la déclaration « Plusieurs cours » : le prof coche
 * ses jours (ou un jour de semaine entier), choisit une heure et une durée par
 * défaut, puis les séances sont générées dans la liste, qui reste modifiable.
 * Helpers purs, sans rendu (SessionCalendar.tsx fait le rendu), testés dans src/Test/Declaration/sessionCalendar.test.ts.
 */

/** Jours de semaine, du lundi au dimanche (isoWeekday 1..7). */
export const WEEKDAYS: Array<{ iso: number; short: string; long: string }> = [
    { iso: 1, short: "Lun", long: "lundi" },
    { iso: 2, short: "Mar", long: "mardi" },
    { iso: 3, short: "Mer", long: "mercredi" },
    { iso: 4, short: "Jeu", long: "jeudi" },
    { iso: 5, short: "Ven", long: "vendredi" },
    { iso: 6, short: "Sam", long: "samedi" },
    { iso: 7, short: "Dim", long: "dimanche" },
];

const DAY = "YYYY-MM-DD";

/**
 * Grille du mois : une ligne par semaine, sept cases du lundi au dimanche,
 * null pour les cases hors du mois.
 */
export const monthGrid = (month: moment.Moment): Array<Array<string | null>> => {
    const first = month.clone().startOf("month");
    const daysInMonth = first.daysInMonth();
    const leading = first.isoWeekday() - 1;
    const cells: Array<string | null> = Array(leading).fill(null);
    for (let day = 1; day <= daysInMonth; day++) {
        cells.push(first.clone().date(day).format(DAY));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks: Array<Array<string | null>> = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
};

/** Un jour est sélectionnable s'il n'est pas avant `minDate` (jour inclus). */
export const isSelectableDay = (date: string, minDate?: string | null): boolean =>
    !minDate || !moment(date).isBefore(moment(minDate), "day");

/** Dates sélectionnables du mois portant ce jour de semaine (isoWeekday 1..7). */
export const weekdayDatesInMonth = (month: moment.Moment, isoWeekday: number, minDate?: string | null): string[] =>
    monthGrid(month)
        .flat()
        .filter((date): date is string => date !== null && moment(date).isoWeekday() === isoWeekday && isSelectableDay(date, minDate));

/**
 * Coche ou décoche un jour. Au-delà de `max` dates, le jour n'est pas ajouté
 * et `refused` vaut true (l'écran affiche la limite).
 */
export const toggleDate = (selected: string[], date: string, max: number): { dates: string[]; refused: boolean } => {
    if (selected.includes(date)) {
        return { dates: selected.filter((d) => d !== date), refused: false };
    }
    if (selected.length >= max) {
        return { dates: selected, refused: true };
    }
    return { dates: [...selected, date].sort(), refused: false };
};

/**
 * Raccourci « tous les mardis » : si toutes les dates sélectionnables de ce
 * jour de semaine sont déjà cochées, elles sont décochées ; sinon les
 * manquantes sont ajoutées, dans la limite de `max`.
 */
export const toggleWeekday = (
    selected: string[],
    month: moment.Moment,
    isoWeekday: number,
    max: number,
    minDate?: string | null
): { dates: string[]; refused: boolean } => {
    const candidates = weekdayDatesInMonth(month, isoWeekday, minDate);
    if (candidates.length === 0) return { dates: selected, refused: false };
    const allSelected = candidates.every((d) => selected.includes(d));
    if (allSelected) {
        return { dates: selected.filter((d) => !candidates.includes(d)), refused: false };
    }
    const dates = [...selected];
    let refused = false;
    for (const date of candidates) {
        if (dates.includes(date)) continue;
        if (dates.length >= max) {
            refused = true;
            break;
        }
        dates.push(date);
    }
    return { dates: dates.sort(), refused };
};

/** Le jour de semaine est « actif » quand toutes ses dates sélectionnables sont cochées. */
export const isWeekdayFullySelected = (selected: string[], month: moment.Moment, isoWeekday: number, minDate?: string | null): boolean => {
    const candidates = weekdayDatesInMonth(month, isoWeekday, minDate);
    return candidates.length > 0 && candidates.every((d) => selected.includes(d));
};

/** Dates (YYYY-MM-DD) des lignes datées, sans doublon, triées. */
export const selectedDatesFromRows = (rows: SessionRow[]): string[] =>
    Array.from(new Set(rows.map((row) => row.date).filter((d): d is string => !!d))).sort();

/**
 * Mois à afficher à l'ouverture du calendrier : celui des séances déjà
 * datées, sinon le mois en cours (ou celui de `minDate` s'il est plus tard).
 */
export const initialCalendarMonth = (rows: SessionRow[], minDate?: string | null): moment.Moment => {
    const dates = selectedDatesFromRows(rows);
    if (dates.length > 0) return moment(dates[0]).startOf("month");
    const current = moment().startOf("month");
    return minDate && moment(minDate).isAfter(current) ? moment(minDate).startOf("month") : current;
};

/** Heure et durée par défaut du calendrier. */
export interface CalendarDefaults {
    startTime: string | null;
    duration: number;
}

const timeKey = (iso: string | null): string | null => (iso ? moment(iso).format("HH:mm") : null);

/**
 * Lignes de séances issues des dates cochées :
 * - une date déjà présente dans la liste garde sa ligne ; une date cochée deux
 *   fois dans la liste (deux séances le même jour) garde ses deux lignes ;
 * - une date nouvelle reçoit l'heure et la durée par défaut ;
 * - si l'heure (ou la durée) par défaut a été modifiée dans le calendrier par
 *   rapport à `previous` (sa valeur à l'ouverture), elle s'applique à toutes
 *   les lignes ; sinon chaque ligne garde l'heure et la durée ajustées à la main ;
 * - une ligne dont la date a été décochée disparaît ;
 * - une ligne sans date mais avec une heure (5e occurrence d'un brouillon)
 *   est conservée ; une ligne entièrement vide (nouvelle déclaration) disparaît.
 */
export const rowsFromSelectedDates = (
    dates: string[],
    existing: SessionRow[],
    defaults: CalendarDefaults,
    previous?: CalendarDefaults
): SessionRow[] => {
    const applyTime = !!defaults.startTime && (!previous || timeKey(previous.startTime) !== timeKey(defaults.startTime));
    const applyDuration = !previous || previous.duration !== defaults.duration;
    const kept = existing
        .filter((row) => (row.date === null ? row.startTime !== null : dates.includes(row.date)))
        .map((row) => ({
            ...row,
            startTime: applyTime || !row.startTime ? defaults.startTime : row.startTime,
            duration: applyDuration ? defaults.duration : row.duration,
        }));
    const known = new Set(kept.map((row) => row.date).filter(Boolean));
    const created = dates
        .filter((date) => !known.has(date))
        .map((date) => ({
            ...createEmptySessionRow(),
            date,
            startTime: defaults.startTime,
            duration: defaults.duration,
        }));
    return sortSessionRows([...kept, ...created]);
};

/**
 * Heure proposée à l'ouverture du calendrier quand aucune séance n'a encore
 * d'heure : l'heure actuelle arrondie aux 5 minutes supérieures (même règle
 * que le formulaire « Un seul cours »).
 */
export const defaultStartTime = (now: moment.Moment = moment()): string => {
    const rounded = now.clone().startOf("minute");
    const remainder = rounded.minute() % 5;
    if (remainder !== 0) rounded.add(5 - remainder, "minute");
    return rounded.toDate().toISOString();
};

/** Heure la plus fréquente parmi les lignes datées, pour pré-remplir l'heure par défaut. */
export const mostCommonStartTime = (rows: SessionRow[]): string | null => {
    const counts = new Map<string, { count: number; iso: string }>();
    rows.forEach((row) => {
        const start = sessionStart(row);
        if (!start || !row.startTime) return;
        const key = start.format("HH:mm");
        const entry = counts.get(key) ?? { count: 0, iso: row.startTime };
        counts.set(key, { count: entry.count + 1, iso: entry.iso });
    });
    const best = [...counts.values()].sort((a, b) => b.count - a.count)[0];
    return best ? best.iso : null;
};

/** Durée la plus fréquente parmi les lignes, sinon 1 h 30. */
export const mostCommonDuration = (rows: SessionRow[], fallback = 1.5): number => {
    const counts = new Map<number, number>();
    rows.forEach((row) => {
        if (row.duration > 0) counts.set(row.duration, (counts.get(row.duration) ?? 0) + 1);
    });
    const best = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    return best ? best[0] : fallback;
};
